'use client';

import { useEffect, useState } from 'react';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import type { LiveV1ComplianceStatusReply } from '../../types';
import { getComplianceStep, type IdentityFormError, isValidChineseIdNumber } from './utils';

const LIVE_COMPLIANCE_COPY = {
  identityTitle: '实名认证',
  identityDescriptionPrefix:
    '根据国家相关政策要求，您需要完成实名认证。未满18周岁的用户将无法使用相关服务。您的信息将进行严格的隐私保护。',
  identityConsentPrefix: '完成认证点击前，请先勾选同意',
  identityConsentLinkText: '《Vidu AI - 敏感个人信息收集处理授权同意书（身份证号码）》',
  realName: '姓名',
  idNumber: '身份证号',
  invalidIdNumber: '请输入正确的身份证号',
  verify: '完成认证',
  minorTitle: '未成年人使用限制',
  minorDescription: '根据平台安全与合规政策，未满18周岁用户无法继续使用本产品\n如有疑问，可联系监护人协助使用',
  submit: '提交',
  seniorTitle: '温馨提示',
  seniorDescription:
    '尊敬的长者朋友：\n本服务为人工智能互动服务，请以身体感受为先，注意劳逸结合，多起身活动。\n我们绝不会以任何理由向您索要银行卡密码、验证码或要求转账。如有陌生人借此联系您，请立即挂断并告知家人。\n如有任何问题，您可联系【support@vidu.cn】我们的客服将为您提供优先人工服务，祝您健康安心！',
  confirm: '我已知晓',
};

interface LiveComplianceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: LiveV1ComplianceStatusReply | null;
  submitting: boolean;
  identityError?: IdentityFormError;
  onIdentityErrorChange: (error?: IdentityFormError) => void;
  onIdentitySubmit: (body: { real_name: string; id_no: string }) => void;
  onSeniorNoticeConfirm: () => void;
}

export function LiveComplianceModal({
  open,
  onOpenChange,
  status,
  submitting,
  identityError,
  onIdentityErrorChange,
  onIdentitySubmit,
  onSeniorNoticeConfirm,
}: LiveComplianceModalProps) {
  const copy = LIVE_COMPLIANCE_COPY;
  const [realName, setRealName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [identityConsentAgreed, setIdentityConsentAgreed] = useState(false);
  const step = status ? getComplianceStep(status) : 'identity';
  const requiresExplicitClose = step === 'identity';
  const identityConsentLabelId = 'live-identity-consent-label';

  useEffect(() => {
    if (!open) {
      setRealName('');
      setIdNumber('');
      setIdentityConsentAgreed(false);
    }
  }, [open]);

  const identityValid = Boolean(realName.trim()) && isValidChineseIdNumber(idNumber);
  const identitySubmittable = identityValid && identityConsentAgreed;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        disableOverlayClose={requiresExplicitClose}
        className="w-[min(480px,calc(100%-32px))] max-w-none sm:p-4"
      >
        {step === 'minor' ? (
          <DialogHeader className="gap-3">
            <DialogTitle>{copy.minorTitle}</DialogTitle>
            <DialogDescription className="whitespace-pre-line text-sm leading-6">
              {copy.minorDescription}
            </DialogDescription>
          </DialogHeader>
        ) : step === 'senior' ? (
          <>
            <DialogHeader className="gap-3">
              <DialogTitle>{copy.seniorTitle}</DialogTitle>
              <DialogDescription className="whitespace-pre-line text-sm leading-6">
                {copy.seniorDescription}
              </DialogDescription>
            </DialogHeader>
            <Button variant="primary" size="dialog" loading={submitting} onClick={onSeniorNoticeConfirm}>
              {copy.confirm}
            </Button>
          </>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (identityValid) onIdentitySubmit({ real_name: realName.trim(), id_no: idNumber.trim().toUpperCase() });
            }}
          >
            <DialogHeader className="gap-2">
              <DialogTitle>{copy.identityTitle}</DialogTitle>
              <DialogDescription className="text-sm leading-6">{copy.identityDescriptionPrefix}</DialogDescription>
            </DialogHeader>
            <Input
              value={realName}
              onChange={(event) => {
                setRealName(event.target.value);
                if (identityError?.realName || identityError?.form) onIdentityErrorChange();
              }}
              placeholder={copy.realName}
              maxLength={25}
            />
            {identityError?.realName && <p className="text-sm text-system-error1">{identityError.realName}</p>}
            <Input
              value={idNumber}
              onChange={(event) => {
                setIdNumber(event.target.value);
                if (identityError?.idNumber || identityError?.form) onIdentityErrorChange();
              }}
              placeholder={copy.idNumber}
              maxLength={18}
            />
            {(identityError?.idNumber || (idNumber && !isValidChineseIdNumber(idNumber))) && (
              <p className="text-sm text-system-error1">{identityError?.idNumber ?? copy.invalidIdNumber}</p>
            )}
            {identityError?.form && <p className="text-sm text-system-error1">{identityError.form}</p>}
            <Button
              className="w-full"
              variant="primary"
              size="dialog"
              loading={submitting}
              disabled={!identitySubmittable}
            >
              {copy.verify}
            </Button>
            <div className="flex items-start gap-3 text-sm text-system-white64 leading-6">
              <Checkbox
                aria-labelledby={identityConsentLabelId}
                className="mt-0.5 shrink-0 text-base text-system-white"
                checked={identityConsentAgreed}
                onCheckedChange={(checked) => setIdentityConsentAgreed(Boolean(checked))}
              />
              <span id={identityConsentLabelId} className="min-w-0 flex-1">
                {copy.identityConsentPrefix}
                <ClientLink
                  className="mx-1 text-system-white underline underline-offset-4"
                  href="/consent-identity"
                  rel="noreferrer"
                  target="_blank"
                >
                  {copy.identityConsentLinkText}
                </ClientLink>
              </span>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
